import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  fetchTelegramAppStatus,
  telegramAppLink,
  telegramAppLogin,
} from "../lib/telegramApp";
import { AuthAlert, AuthShell } from "../components/auth/AuthShell";
import { Loader } from "../components/ui/Loader";

declare global {
  interface Window {
    Telegram?: { WebApp?: { initData: string; ready: () => void; expand: () => void } };
  }
}

type Phase =
  | "init"
  | "not-telegram"
  | "checking"
  | "conflict"
  | "done";

// Runs only inside Telegram's WebView (opened via the bot's Mini App button).
// - Already linked?              -> silent auto-login, no form at all.
// - Signed in, not linked?       -> link automatically.
// - Not signed in, not linked?   -> use normal login/signup, then auto-link.
export const TelegramApp = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("init");
  const initDataRef = useRef<string | null>(null);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg || !tg.initData) {
      setPhase("not-telegram");
      return;
    }
    tg.ready();
    tg.expand();
    initDataRef.current = tg.initData;
    setPhase("checking");
  }, []);

  useEffect(() => {
    if (phase !== "checking" || authLoading) return;
    const initData = initDataRef.current;
    if (!initData) return;

    let cancelled = false;
    (async () => {
      try {
        if (!user) {
          const result = await telegramAppLogin(initData);
          if (cancelled) return;
          if (result.linked) {
            setPhase("done");
          } else {
            navigate("/login?telegram=true", { replace: true });
          }
        } else {
          const status = await fetchTelegramAppStatus(initData);
          if (cancelled) return;
          if (!status.linkedUserId) {
            // Silent attempt: any failure just leaves Telegram unlinked.
            await telegramAppLink(initData).catch(() => {});
            if (!cancelled) setPhase("done");
          } else if (status.linkedUserId === user.id) {
            setPhase("done");
          } else {
            setPhase("conflict");
          }
        }
      } catch {
        if (cancelled) return;
        if (user) setPhase("done");
        else navigate("/login?telegram=true", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, authLoading, user, navigate]);

  useEffect(() => {
    if (phase === "done") navigate("/dashboard", { replace: true });
  }, [phase, navigate]);

  if (phase === "not-telegram") {
    return (
      <AuthShell title="Neoteplo Partner" subtitle="Telegram App">
        <br/>
        <AuthAlert kind="error" message="Ця сторінка працює лише всередині Telegram." />
      </AuthShell>
    );
  }

  if (phase === "conflict") {
    return (
      <AuthShell title="Telegram вже підключено" subtitle="До іншого акаунту">
        <br/>
        <AuthAlert
          kind="error"
          message="Цей Telegram-акаунт вже підключено до іншого користувача. Спершу відключіть його в іншому акаунті."
        />
      </AuthShell>
    );
  }

  return <Loader />;
};
